import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Briefcase, 
  Plus, 
  FileText, 
  Trash2, 
  Edit2, 
  MoreHorizontal,
  Upload,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Target,
  MessageSquare,
  Clock,
  Link as LinkIcon,
  AlertCircle,
  RefreshCw,
  Loader2,
  LogOut,
  User,
  PanelLeftClose,
  PanelLeft,
  Copy,
  Check,
  ChevronRight,
  ChevronDown,
  Star,
  ThumbsUp,
  ThumbsDown,
  Crown,
  Shield,
  Zap,
  CreditCard,
  Send,
  Bot,
  Settings,
  HelpCircle,
  Bug,
  Users,
  UserCheck,
  Gift,
  Code,
  PanelRight,
  PanelRightClose,
  FileText as FileTextIcon,
  Building2,
  TrendingUp,
  BookOpen,
  Lightbulb,
  DollarSign,
  Network,
  ExternalLink,
  Lock,
  Linkedin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 180000,  // 3 minute default timeout
  withCredentials: true
});

// Add Authorization header and impersonation token from localStorage
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('hireun_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add impersonation token if in impersonation mode
  const impersonationToken = localStorage.getItem('hireun_impersonation_token');
  if (impersonationToken) {
    config.headers['X-Impersonation-Token'] = impersonationToken;
  }
  
  return config;
});

const FREE_TIER_LIMIT = 5;

const INTERVIEW_MODES = {
  hr_recruiter: { name: "HR / Recruiter Screen", focus: "soft skills, motivation, communication" },
  hiring_manager: { name: "Hiring Manager", focus: "ownership, impact, leadership" },
  technical_expert: { name: "Technical Expert", focus: "technical depth, tools, architecture" }
};

const AppDashboard = ({ user: initialUser, impersonationData }) => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  
  // User state with access info - use impersonated user if in impersonation mode
  const [user, setUser] = useState(impersonationData?.target_user || initialUser);
  
  // Track if we're in impersonation mode
  const isImpersonationMode = !!impersonationData;
  
  // State
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  
  // Workflow state
  const [step, setStep] = useState(1);
  const [cvFile, setCvFile] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [jobInputType, setJobInputType] = useState("text");
  const [jobUrl, setJobUrl] = useState("");
  const [jobText, setJobText] = useState("");
  const [jobData, setJobData] = useState(null);
  const [interviewType, setInterviewType] = useState("general");
  const [interviewMode, setInterviewMode] = useState("hr_recruiter");
  
  // CV Panel state
  const [showCVPanel, setShowCVPanel] = useState(false);
  const [cvDocumentUrl, setCvDocumentUrl] = useState(null);
  const [cvViewMode, setCvViewMode] = useState("document"); // "document" or "text"
  const [isLoadingCvDocument, setIsLoadingCvDocument] = useState(false);
  
  // Results state
  const [copiedSection, setCopiedSection] = useState(null);
  const [activeTab, setActiveTab] = useState("scripts");
  
  // Career Insights state (embedded in results)
  const [showInsights, setShowInsights] = useState(false);
  const [insightsTab, setInsightsTab] = useState("intelligence");
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [salaryData, setSalaryData] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  
  // Chat state for AI refinement
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  
  // Support ticket state
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportCategory, setSupportCategory] = useState("question");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [isRedeemingPromo, setIsRedeemingPromo] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);

  // Check for payment callback
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      setIsCheckingPayment(true);
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error("Payment was cancelled");
      // Clean URL
      navigate('/app', { replace: true });
    }
  }, [searchParams]);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setIsCheckingPayment(false);
      toast.error("Payment verification timed out. Please refresh the page.");
      navigate('/app', { replace: true });
      return;
    }

    try {
      const response = await apiClient.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setIsCheckingPayment(false);
        toast.success("🎉 Payment successful! You now have unlimited access!");
        
        // Refresh user data
        const userResponse = await apiClient.get('/auth/me');
        setUser(userResponse.data);
        
        navigate('/app', { replace: true });
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status check error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  // Fetch user data and sessions
  useEffect(() => {
    const fetchData = async () => {
      try {
        // If in impersonation mode, we already have the user data
        // Just fetch sessions (which will use the impersonation token via interceptor)
        if (isImpersonationMode) {
          const sessionsRes = await apiClient.get('/sessions');
          setSessions(sessionsRes.data.sessions || []);
          // Don't update user - keep the impersonated user
          return;
        }
        
        // Normal mode - fetch both user and sessions
        const [userRes, sessionsRes] = await Promise.all([
          apiClient.get('/auth/me'),
          apiClient.get('/sessions')
        ]);
        setUser(userRes.data);
        setSessions(sessionsRes.data.sessions || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
  }, [isImpersonationMode]);

  // Load session from URL
  useEffect(() => {
    if (urlSessionId && sessions.length > 0) {
      const session = sessions.find(s => s.session_id === urlSessionId);
      if (session) {
        loadSession(session);
      }
    }
  }, [urlSessionId, sessions]);

  // Check for generated prep to save (user came from results page after generating anonymously)
  useEffect(() => {
    const saveGeneratedPrep = async () => {
      try {
        const generatedPrepData = sessionStorage.getItem('hireun_generated_prep');
        if (generatedPrepData && user) {
          const prepData = JSON.parse(generatedPrepData);
          
          // Only use if less than 1 hour old
          if (Date.now() - prepData.timestamp < 3600000) {
            // Clear the stored data immediately to prevent duplicate processing
            sessionStorage.removeItem('hireun_generated_prep');
            
            // Handle both naming conventions (content or interviewData)
            const interviewData = prepData.content || prepData.interviewData;
            const jobInfo = prepData.jobData || interviewData;
            
            // Create a new session with the generated data
            const sessionName = jobInfo?.title || interviewData?.job_title
              ? `${jobInfo?.title || interviewData?.job_title} at ${jobInfo?.company || interviewData?.company_name || 'Company'}`
              : `Interview Prep ${sessions.length + 1}`;
            
            const response = await apiClient.post("/sessions", { name: sessionName });
            const newSession = response.data;
            
            // Save the generated content to the session
            await apiClient.patch(`/sessions/${newSession.session_id}`, {
              cv_text: prepData.cvData?.text || "",
              job_description: jobInfo?.description || interviewData?.job_description || "",
              job_title: jobInfo?.title || interviewData?.job_title || "Position",
              company_name: jobInfo?.company || interviewData?.company_name || "Company",
              interview_mode: prepData.interviewType || "general",
              generated_content: interviewData?.content || interviewData
            });
            
            // Update sessions list and set active with the content
            const sessionWithContent = {
              ...newSession,
              content: interviewData?.content || interviewData
            };
            setSessions(prev => [sessionWithContent, ...prev]);
            setActiveSession(sessionWithContent);
            
            // Set the data in state
            if (prepData.cvData) {
              setCvData(prepData.cvData);
              setCvFile({ name: "CV uploaded" });
            }
            if (jobInfo) {
              setJobData(jobInfo);
            }
            setStep(4); // Go to results step
            
            toast.success("Your interview prep has been saved to your account!");
            navigate(`/app/session/${newSession.session_id}`, { replace: true });
          } else {
            // Data is too old, clear it
            sessionStorage.removeItem('hireun_generated_prep');
          }
        }
      } catch (error) {
        console.error("Error saving generated prep:", error);
        toast.error("Failed to save your prep. Please try again.");
        sessionStorage.removeItem('hireun_generated_prep');
      }
    };
    
    // Only run if we have user data
    if (user && !activeSession && !urlSessionId) {
      saveGeneratedPrep();
    }
  }, [user, activeSession, urlSessionId]);

  // Pre-fetch Career Intelligence in background when content is displayed
  useEffect(() => {
    const prefetchIntelligence = async () => {
      // Only prefetch if:
      // 1. We have content displayed (step 4)
      // 2. We have a valid company to research
      // 3. Intelligence data hasn't been fetched yet
      // 4. Not already loading
      if (step === 4 && activeSession?.content && canGenerateInsights() && !intelligenceData && !isLoadingInsights) {
        console.log("Pre-fetching Career Intelligence in background...");
        try {
          const ctx = getJobContext();
          const response = await apiClient.post("/insights/intelligence", {
            company_name: ctx.company,
            job_title: ctx.job_title,
            job_description: activeSession?.job_description || "",
            sector: ctx.industry || ""
          });
          
          if (response.data.success && response.data.intelligence) {
            setIntelligenceData(response.data.intelligence);
            console.log("Career Intelligence pre-fetched successfully");
          }
        } catch (error) {
          // Silent fail for background fetch - user can retry manually
          console.log("Background intelligence fetch failed:", error.message);
        }
      }
    };
    
    // Small delay to not block initial render
    const timer = setTimeout(prefetchIntelligence, 1500);
    return () => clearTimeout(timer);
  }, [step, activeSession?.session_id, activeSession?.content]);

  const loadSession = (session) => {
    setActiveSession(session);
    
    // Reset intelligence data when switching sessions (different company)
    setIntelligenceData(null);
    setSalaryData(null);
    
    if (session.cv_text) {
      setCvData({ text: session.cv_text, skills: [], experiences: [] });
      setCvFile({ name: session.cv_filename || "CV uploaded" });
    }
    
    if (session.job_description) {
      setJobData({
        title: session.job_title || "Job Position",
        company: session.company_name || "Company",
        description: session.job_description
      });
      setJobText(session.job_description);
    }
    
    if (session.interview_type) {
      setInterviewType(session.interview_type);
    }
    
    if (session.content) {
      setStep(4);
    } else if (session.job_description) {
      setStep(3);
    } else if (session.cv_text) {
      setStep(2);
    } else {
      setStep(1);
    }
    
    navigate(`/app/session/${session.session_id}`, { replace: true });
  };

  const createNewSession = async () => {
    // Check if user can create more sessions (only matters when generating)
    try {
      const response = await apiClient.post("/sessions", {
        name: `Interview Prep ${sessions.length + 1}`
      });
      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      loadSession(newSession);
      resetWorkflow();
    } catch (error) {
      toast.error("Failed to create session");
    }
  };

  const resetWorkflow = () => {
    setStep(1);
    setCvFile(null);
    setCvData(null);
    setJobUrl("");
    setJobText("");
    setJobData(null);
    setInterviewType("general");
    setError(null);
  };

  const updateSession = async (sessionId, data) => {
    // Block write operations in impersonation mode
    if (isImpersonationMode) {
      toast.error("Cannot modify sessions in support mode (read-only)");
      return;
    }
    try {
      const response = await apiClient.patch(`/sessions/${sessionId}`, data);
      // Use the response from backend which may include auto-renamed session
      const updatedSession = response.data;
      setSessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, ...updatedSession } : s
      ));
      if (activeSession?.session_id === sessionId) {
        setActiveSession(prev => ({ ...prev, ...updatedSession }));
      }
    } catch (error) {
      console.error("Failed to update session:", error);
    }
  };

  const deleteSession = async (sessionId) => {
    // Block write operations in impersonation mode
    if (isImpersonationMode) {
      toast.error("Cannot delete sessions in support mode (read-only)");
      return;
    }
    try {
      await apiClient.delete(`/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSession?.session_id === sessionId) {
        setActiveSession(null);
        resetWorkflow();
        navigate("/app", { replace: true });
      }
      toast.success("Session deleted");
    } catch (error) {
      toast.error("Failed to delete session");
    }
  };

  const renameSession = async (sessionId) => {
    // Block write operations in impersonation mode
    if (isImpersonationMode) {
      toast.error("Cannot rename sessions in support mode (read-only)");
      return;
    }
    if (!editingName.trim()) return;
    await updateSession(sessionId, { name: editingName });
    setEditingSessionId(null);
    setEditingName("");
  };

  const handleLogout = async () => {
    // Block logout in impersonation mode - admin should use Exit button
    if (isImpersonationMode) {
      toast.error("Use 'Exit Support Mode' button to end impersonation");
      return;
    }
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      // Proceed with local cleanup even if backend call fails
    }
    localStorage.removeItem('hireun_user');
    localStorage.removeItem('hireun_session_token');
    navigate("/", { replace: true });
  };

  const handleUpgrade = async () => {
    // Block payment actions in impersonation mode
    if (isImpersonationMode) {
      toast.error("Cannot modify subscription in support mode");
      return;
    }
    try {
      const response = await apiClient.post("/payments/checkout", {
        plan_id: "premium_monthly",
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Checkout error:", error);
      if (error.response?.status === 401) {
        toast.error("Please log in again to upgrade.");
        // Could optionally redirect to login
      } else {
        toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
      }
    }
  };

  // Promo Code Redemption
  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setIsRedeemingPromo(true);
    try {
      const response = await apiClient.post("/redeem-promo", {
        code: promoCode.trim().toUpperCase()
      });
      
      toast.success(response.data.message);
      setPromoCode("");
      setShowPromoInput(false);
      setShowUpgradeModal(false);
      
      // Refresh user data to get updated preps/status
      try {
        const userRes = await apiClient.get('/auth/me');
        setUser(userRes.data);
      } catch (refreshError) {
        console.error("Failed to refresh user data:", refreshError);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem promo code");
    } finally {
      setIsRedeemingPromo(false);
    }
  };

  // CV Upload
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setCvFile(file);
    setIsLoading(true);
    setLoadingStep("Parsing your CV...");
    setLoadingProgress(20);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post("/upload-cv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      
      const data = response.data;
      
      // Check for CV validation warning
      if (data.warning_message) {
        if (!data.is_valid_cv) {
          // Document doesn't look like a CV - show warning and ask for confirmation
          toast.warning(data.warning_message, { duration: 8000 });
          const proceed = window.confirm(
            `${data.warning_message}\n\nDo you want to continue anyway, or would you like to upload a different file?`
          );
          if (!proceed) {
            setCvFile(null);
            setIsLoading(false);
            setLoadingStep("");
            setLoadingProgress(0);
            return;
          }
        } else {
          // CV is valid but incomplete - just show info toast
          toast.info(data.warning_message, { duration: 5000 });
        }
      }
      
      setCvData(data);
      setLoadingProgress(100);
      toast.success("CV parsed successfully!");
      
      if (activeSession) {
        await updateSession(activeSession.session_id, {
          cv_text: data.text,
          cv_filename: file.name
        });
      }
      
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to parse CV";
      setError({ type: "cv", message: errorMsg });
      toast.error(errorMsg);
      setCvFile(null);
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  }, [activeSession]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"]
    },
    maxFiles: 1,
    disabled: isLoading
  });

  // Job handling
  const handleJobSubmit = async () => {
    if (jobInputType === "url" && !jobUrl) {
      toast.error("Please enter a job URL");
      return;
    }
    if (jobInputType === "text" && !jobText) {
      toast.error("Please paste the job description");
      return;
    }

    setIsLoading(true);
    setLoadingStep("Analyzing job description...");
    setLoadingProgress(30);
    setError(null);

    try {
      const response = await apiClient.post("/scrape-job", {
        url: jobInputType === "url" ? jobUrl : null,
        manual_text: jobInputType === "text" ? jobText : null
      }, { timeout: 20000 });
      
      setJobData(response.data);
      setLoadingProgress(100);
      toast.success("Job description analyzed!");
      
      if (activeSession) {
        await updateSession(activeSession.session_id, {
          job_title: response.data.title,
          company_name: response.data.company,
          job_description: response.data.description
        });
      }
      
      setStep(3);
    } catch (error) {
      let errorMsg = "Failed to analyze job description.";
      if (error.response?.status === 408 || error.code === "ECONNABORTED") {
        errorMsg = "Job URL request timed out. Please paste manually.";
        setJobInputType("text");
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      setError({ type: "job", message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  };

  // Generate with access check
  const handleGenerate = async () => {
    if (!activeSession) {
      toast.error("Please create or select a session first");
      return;
    }

    // Check if user can generate
    if (!user?.can_generate && !user?.is_admin && user?.role !== "premium") {
      setShowUpgradeModal(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => prev >= 85 ? prev : prev + Math.random() * 5);
    }, 2000);

    try {
      setLoadingStep("Preparing your data...");
      setLoadingProgress(10);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setLoadingStep("Generating personalized content...");
      setLoadingProgress(25);

      await updateSession(activeSession.session_id, { 
        interview_type: interviewType,
        interview_mode: interviewMode 
      });

      const response = await apiClient.post(`/sessions/${activeSession.session_id}/generate`, {}, {
        timeout: 180000,  // 3 minute timeout for generation
      });

      clearInterval(progressInterval);
      setLoadingProgress(100);
      setLoadingStep("Complete!");

      setActiveSession(prev => ({ ...prev, content: response.data.content }));
      setSessions(prev => prev.map(s => 
        s.session_id === activeSession.session_id 
          ? { ...s, content: response.data.content }
          : s
      ));

      // Update user stats
      if (response.data.preps_used !== undefined) {
        setUser(prev => ({
          ...prev,
          preps_used: response.data.preps_used,
          preps_remaining: response.data.preps_remaining,
          can_generate: response.data.preps_remaining > 0 || response.data.preps_remaining === -1
        }));
      }

      toast.success("Interview prep ready!");
      setStep(4);
      
    } catch (error) {
      clearInterval(progressInterval);
      
      // Check if it's a rate limit error
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
        return;
      }
      
      let errorMsg = "Failed to generate interview preparation.";
      if (error.response?.status === 504 || error.code === "ECONNABORTED") {
        errorMsg = "Generation timed out. Please try again.";
      } else if (error.response?.status === 502) {
        errorMsg = "AI service temporarily unavailable. Please try again.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError({ type: "generate", message: errorMsg, canRetry: true });
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  };

  // Regenerate for different interview mode
  const regenerateForMode = async (newMode) => {
    if (!activeSession || isLoading) return;
    
    setIsLoading(true);
    setLoadingStep(`Regenerating for ${INTERVIEW_MODES[newMode].name}...`);
    setLoadingProgress(20);
    setError(null);
    
    try {
      const response = await apiClient.post(`/sessions/${activeSession.session_id}/regenerate-mode`, {
        interview_mode: newMode
      });
      
      setLoadingProgress(90);
      
      // Update session with new content
      setActiveSession(prev => ({
        ...prev,
        content: response.data.content,
        interview_mode: newMode
      }));
      setInterviewMode(newMode);
      
      // Update user quota
      if (response.data.preps_used !== undefined) {
        setUser(prev => ({
          ...prev,
          preps_used: response.data.preps_used,
          preps_remaining: response.data.preps_remaining,
          can_generate: response.data.preps_remaining !== 0
        }));
      }
      
      // Clear chat when mode changes
      setChatMessages([]);
      setShowChat(false);
      
      setStep(4);
      toast.success(`Generated for ${INTERVIEW_MODES[newMode].name}!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to regenerate. Please try again.";
      toast.error(errorMsg);
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      }
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text, sectionId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(sectionId);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const CopyButton = ({ text, sectionId }) => (
    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(text, sectionId)} className="copy-btn">
      {copiedSection === sectionId ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
    </Button>
  );

  // Chat functions for AI refinement
  const loadChatHistory = async (sessionId) => {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/chat`);
      setChatMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  // Fetch CV document URL when panel is opened
  const fetchCvDocumentUrl = async () => {
    if (!activeSession?.session_id || !activeSession?.cv_filename) return;
    
    setIsLoadingCvDocument(true);
    try {
      const response = await apiClient.get(`/sessions/${activeSession.session_id}/document-url`);
      setCvDocumentUrl(response.data);
    } catch (error) {
      console.error("Failed to fetch CV document URL:", error);
      // Fall back to text view if document URL fails
      setCvViewMode("text");
    } finally {
      setIsLoadingCvDocument(false);
    }
  };

  // Load CV document when panel is shown
  useEffect(() => {
    if (showCVPanel && activeSession?.cv_filename && !cvDocumentUrl) {
      fetchCvDocumentUrl();
    }
  }, [showCVPanel, activeSession?.session_id]);

  // Clear CV document URL when session changes
  useEffect(() => {
    setCvDocumentUrl(null);
    setCvViewMode("document");
  }, [activeSession?.session_id]);

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !activeSession) return;
    
    const userMessage = chatInput.trim();
    setChatInput("");
    setIsChatLoading(true);
    
    // Add user message to UI immediately
    const newUserMsg = { role: "user", content: userMessage, timestamp: new Date().toISOString() };
    setChatMessages(prev => [...prev, newUserMsg]);
    
    try {
      const response = await apiClient.post(`/sessions/${activeSession.session_id}/chat`, {
        message: userMessage,
        section: selectedSection
      });
      
      // Add assistant response
      const assistantMsg = { 
        role: "assistant", 
        content: response.data.response, 
        timestamp: new Date().toISOString() 
      };
      setChatMessages(prev => [...prev, assistantMsg]);
      
      // If content was updated, refresh the session
      if (response.data.updated_section && response.data.updated_content) {
        setActiveSession(prev => ({
          ...prev,
          content: {
            ...prev.content,
            [response.data.updated_section]: response.data.updated_content
          }
        }));
        toast.success(`Updated: ${response.data.updated_section.replace(/_/g, ' ')}`);
      }
    } catch (error) {
      toast.error("Failed to send message");
      setChatMessages(prev => prev.slice(0, -1)); // Remove the user message on error
    } finally {
      setIsChatLoading(false);
    }
  };

  const quickChatPrompts = [
    "Make this more concise",
    "Add concrete metrics",
    "Make it more confident",
    "Rewrite for a senior role",
    "Make it sound more natural",
    "Drill deeper on this topic",
    "Challenge this answer"
  ];

  // Mode-specific prompts for chat
  const getModePrompts = () => {
    const mode = activeSession?.interview_mode || interviewMode;
    if (mode === "technical_expert") {
      return ["Add more technical depth", "Include specific tools/tech", "Explain the architecture", "Add trade-offs discussion"];
    } else if (mode === "hiring_manager") {
      return ["Emphasize leadership impact", "Add business metrics", "Show ownership examples", "Highlight decision-making"];
    }
    return ["Show cultural fit", "Add motivation examples", "Make more conversational", "Emphasize soft skills"];
  };

  // Career Insights Functions
  const getJobContext = () => {
    const company = activeSession?.company_name || jobData?.company;
    const jobTitle = activeSession?.job_title || jobData?.title;
    
    // Check if we have a valid company (not placeholder)
    const isValidCompany = company && 
      !["company", "entreprise", "société", "the company", "our company"].includes(company.toLowerCase());
    
    return {
      job_title: jobTitle || "",
      company: isValidCompany ? company : null,
      location: "Remote",  // Could be extracted from job description
      seniority: "mid",
      industry: "",
      language: "en",
      hasValidCompany: isValidCompany
    };
  };
  
  // Check if insights can be generated
  const canGenerateInsights = () => {
    const ctx = getJobContext();
    return ctx.hasValidCompany && ctx.job_title;
  };

  const fetchCompanyIntelligence = async () => {
    const ctx = getJobContext();
    if (!ctx.hasValidCompany) {
      toast.error("A specific company name is required to generate intelligence");
      return;
    }
    
    setIsLoadingInsights(true);
    try {
      const response = await apiClient.post("/insights/intelligence", {
        company_name: ctx.company,
        job_title: ctx.job_title,
        job_description: activeSession?.job_description || "",
        sector: ctx.industry || ""
      });
      
      // Check for company validation error
      if (response.data.error === "company_not_found") {
        toast.error(response.data.message || "Could not find company information");
        setIntelligenceData({ error: response.data.message });
        return;
      }
      
      setIntelligenceData(response.data.intelligence);
    } catch (error) {
      toast.error("Failed to generate intelligence");
      console.error(error);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const fetchSalaryBenchmark = async () => {
    const ctx = getJobContext();
    
    setIsLoadingInsights(true);
    try {
      const response = await apiClient.post("/insights/salary", {
        job_title: ctx.job_title,
        seniority: ctx.seniority,
        location: ctx.location,
        company_name: ctx.company || "",  // Can work without company
        industry: ctx.industry,
        language: ctx.language
      });
      
      if (response.data.is_premium_feature) {
        setSalaryData({ isPremiumLocked: true, preview: response.data.preview });
      } else {
        setSalaryData(response.data.salary);
      }
    } catch (error) {
      toast.error("Failed to get salary data");
      console.error(error);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const handleInsightTabChange = (tab) => {
    setInsightsTab(tab);
    // Auto-fetch data when tab is selected
    if (tab === "intelligence" && !intelligenceData) fetchCompanyIntelligence();
    if (tab === "salary" && !salaryData) fetchSalaryBenchmark();
  };

  // Support ticket submission
  const submitSupportTicket = async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setIsSubmittingTicket(true);
    try {
      await apiClient.post("/support/tickets", {
        category: supportCategory,
        subject: supportSubject.trim(),
        message: supportMessage.trim(),
        session_id: activeSession?.session_id
      });
      
      toast.success("Support ticket submitted! We'll get back to you soon.");
      setShowSupportModal(false);
      setSupportSubject("");
      setSupportMessage("");
      setSupportCategory("question");
    } catch (error) {
      toast.error("Failed to submit ticket");
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const content = activeSession?.content;

  // User role badge
  const getUserBadge = () => {
    if (user?.is_admin) {
      return <Badge className="bg-red-500 text-white"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
    }
    if (user?.role === "premium") {
      return <Badge className="bg-amber-500 text-white"><Crown className="w-3 h-3 mr-1" />Premium</Badge>;
    }
    return <Badge variant="secondary">Free</Badge>;
  };

  // Usage indicator
  const UsageIndicator = () => {
    if (user?.is_admin || user?.role === "premium") {
      return (
        <div className="px-3 py-2 text-xs text-green-500 flex items-center gap-1">
          <Zap className="w-3 h-3" />
          Unlimited access
        </div>
      );
    }
    
    const used = user?.preps_used || 0;
    const remaining = FREE_TIER_LIMIT - used;
    const percentage = (used / FREE_TIER_LIMIT) * 100;
    
    return (
      <div className="px-3 py-2">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-slate-400">Free preps used</span>
          <span className={remaining <= 2 ? "text-amber-400" : "text-slate-300"}>{used}/{FREE_TIER_LIMIT}</span>
        </div>
        <Progress value={percentage} className="h-1.5" />
        {remaining <= 3 && remaining > 0 && (
          <p className="text-xs text-amber-400 mt-1">{remaining} remaining</p>
        )}
        {remaining === 0 && (
          <Button 
            size="sm" 
            className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-xs"
            onClick={() => setShowUpgradeModal(true)}
          >
            <Crown className="w-3 h-3 mr-1" />
            Upgrade to Premium
          </Button>
        )}
      </div>
    );
  };

  // Payment checking overlay
  if (isCheckingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-96">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Verifying Payment</h3>
            <p className="text-sm text-muted-foreground">Please wait while we confirm your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Upgrade to Premium
            </DialogTitle>
            <DialogDescription>
              You've reached your free limit of {FREE_TIER_LIMIT} interview preps.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg p-4 border border-amber-500/20">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">Premium Monthly</span>
                <span className="text-2xl font-bold">€3.99<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
              </div>
              <ul className="space-y-2 text-sm">
                {["Unlimited interview preps", "Unlimited chat refinements", "No ads", "Priority generation", "Cancel anytime"].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Promo Code Section */}
            <div className="border-t pt-4">
              {!showPromoInput ? (
                <button 
                  onClick={() => setShowPromoInput(true)}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                  data-testid="have-promo-code-btn"
                >
                  <Gift className="w-4 h-4" />
                  Have a promo code?
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter promo code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="flex-1"
                      data-testid="promo-code-input"
                    />
                    <Button 
                      onClick={handleRedeemPromo} 
                      disabled={isRedeemingPromo || !promoCode.trim()}
                      data-testid="redeem-promo-btn"
                    >
                      {isRedeemingPromo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Redeem"
                      )}
                    </Button>
                  </div>
                  <button 
                    onClick={() => { setShowPromoInput(false); setPromoCode(""); }}
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
              Maybe Later
            </Button>
            <Button onClick={handleUpgrade} className="bg-amber-500 hover:bg-amber-600">
              <CreditCard className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="bg-slate-900 text-white flex flex-col h-screen overflow-hidden"
          >
            {/* Sidebar Header */}
            <div className="p-4 border-b border-slate-700">
              {isImpersonationMode ? (
                <div className="text-center text-slate-400 text-sm py-2">
                  <span className="block font-medium text-slate-300">Read-Only Mode</span>
                  <span className="text-xs">Viewing user's sessions</span>
                </div>
              ) : (
                <Button 
                  onClick={createNewSession}
                  className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600"
                  data-testid="new-session-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Interview Prep
                </Button>
              )}
            </div>

            {/* Usage Indicator */}
            <div className="border-b border-slate-700">
              <UsageIndicator />
            </div>

            {/* Sessions List */}
            <div className="flex-1 overflow-y-auto p-2">
              <div className="text-xs text-slate-400 px-2 py-2 font-medium">
                Your Sessions
              </div>
              {sessions.length === 0 ? (
                <div className="text-center text-slate-500 py-8">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No sessions yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        activeSession?.session_id === session.session_id
                          ? "bg-slate-700"
                          : "hover:bg-slate-800"
                      }`}
                      onClick={() => loadSession(session)}
                      data-testid={`session-${session.session_id}`}
                    >
                      {session.content ? (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      )}
                      
                      {editingSessionId === session.session_id ? (
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => renameSession(session.session_id)}
                          onKeyDown={(e) => e.key === 'Enter' && renameSession(session.session_id)}
                          className="h-6 bg-slate-800 border-slate-600 text-sm"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="flex-1 text-sm truncate">{session.name}</span>
                      )}
                      
                      {/* Hide action menu in impersonation mode (read-only) */}
                      {!isImpersonationMode && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setEditingSessionId(session.session_id);
                              setEditingName(session.name);
                            }}>
                              <Edit2 className="w-4 h-4 mr-2" />Rename
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-500" onClick={(e) => {
                              e.stopPropagation();
                              deleteSession(session.session_id);
                            }}>
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* User Section */}
            <div className="p-4 border-t border-slate-700">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-full justify-start text-left hover:bg-slate-800">
                    <div className="flex items-center gap-3 w-full">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                          <User className="w-4 h-4" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{user?.name}</p>
                          {getUserBadge()}
                        </div>
                        <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {user?.is_admin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin")}>
                        <Shield className="w-4 h-4 mr-2 text-red-500" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {user?.role !== "premium" && !user?.is_admin && (
                    <>
                      <DropdownMenuItem onClick={() => setShowUpgradeModal(true)}>
                        <Crown className="w-4 h-4 mr-2 text-amber-500" />
                        Upgrade to Premium
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b border-border bg-white flex items-center px-4 gap-4">
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
          </Button>
          
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" />
            <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {activeSession?.name || "Hireun"}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowSupportModal(true)}
              className="text-muted-foreground"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Support</span>
            </Button>
            {user?.is_admin && (
              <Badge className="bg-red-500 text-white">
                <Shield className="w-3 h-3 mr-1" />Admin Mode
              </Badge>
            )}
            {user?.role === "premium" && !user?.is_admin && (
              <Badge className="bg-amber-500 text-white">
                <Crown className="w-3 h-3 mr-1" />Premium
              </Badge>
            )}
          </div>

          {activeSession && step < 4 && (
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    step >= s ? "bg-primary text-white" : "bg-secondary text-muted-foreground"
                  }`}>
                    {step > s ? <CheckCircle className="w-3.5 h-3.5" /> : s}
                  </div>
                  {s < 3 && <div className={`w-8 h-0.5 mx-1 ${step > s ? "bg-primary" : "bg-secondary"}`} />}
                </div>
              ))}
            </div>
          )}
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {!activeSession ? (
            /* Empty State */
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md px-4">
                <Briefcase className="w-16 h-16 text-muted-foreground/30 mx-auto mb-6" />
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {isImpersonationMode ? `Viewing ${user?.name || user?.email}'s Account` : "Welcome to Hireun"}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {isImpersonationMode 
                    ? `This user has ${sessions.length} prep session(s). Select one from the sidebar to view.`
                    : "Create a new session to start preparing for your interview with AI-powered personalized content."
                  }
                </p>
                {!isImpersonationMode && (
                  <Button onClick={createNewSession} size="lg" data-testid="start-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Start New Prep
                  </Button>
                )}
              </div>
            </div>
          ) : step === 4 && content ? (
            /* Results View */
            <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Your Interview Preparation
                    </h1>
                    <p className="text-muted-foreground">
                      Personalized content for {jobData?.title || activeSession.job_title}
                      {(jobData?.company || activeSession.company_name) && 
                       !["company", "entreprise", "société"].includes((jobData?.company || activeSession.company_name || "").toLowerCase()) && 
                       ` at ${jobData?.company || activeSession.company_name}`}
                    </p>
                  </div>
                  {/* CV Panel Toggle */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCVPanel(!showCVPanel)}
                    className="hidden md:flex"
                  >
                    {showCVPanel ? <PanelRightClose className="w-4 h-4 mr-2" /> : <PanelRight className="w-4 h-4 mr-2" />}
                    {showCVPanel ? "Hide CV" : "Show CV"}
                  </Button>
                </div>
                
                {/* Interview Mode Indicator & Switcher */}
                <div className="p-4 bg-secondary/50 rounded-lg border">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        {(activeSession?.interview_mode || interviewMode) === "hr_recruiter" && <Users className="w-5 h-5 text-primary" />}
                        {(activeSession?.interview_mode || interviewMode) === "hiring_manager" && <UserCheck className="w-5 h-5 text-primary" />}
                        {(activeSession?.interview_mode || interviewMode) === "technical_expert" && <Code className="w-5 h-5 text-primary" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {INTERVIEW_MODES[activeSession?.interview_mode || interviewMode]?.name || "HR / Recruiter Screen"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Focus: {INTERVIEW_MODES[activeSession?.interview_mode || interviewMode]?.focus}
                        </p>
                      </div>
                    </div>
                    
                    {/* Quick Mode Switch */}
                    <div className="flex gap-2">
                      {Object.entries(INTERVIEW_MODES).map(([key, mode]) => {
                        const isCurrentMode = (activeSession?.interview_mode || interviewMode) === key;
                        const isLoadingThisMode = isLoading && loadingStep.includes(mode.name);
                        return (
                          <Button
                            key={key}
                            variant={isCurrentMode ? "default" : "outline"}
                            size="sm"
                            onClick={() => !isCurrentMode && regenerateForMode(key)}
                            disabled={isLoading || isCurrentMode}
                            className={`text-xs transition-all ${isLoadingThisMode ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                            data-testid={`mode-btn-${key}`}
                          >
                            {isLoadingThisMode ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <>
                                {key === "hr_recruiter" && <Users className="w-3 h-3 mr-1" />}
                                {key === "hiring_manager" && <UserCheck className="w-3 h-3 mr-1" />}
                                {key === "technical_expert" && <Code className="w-3 h-3 mr-1" />}
                              </>
                            )}
                            <span className="hidden sm:inline">{mode.name.split(" / ")[0]}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Loading Overlay for Regeneration */}
              {isLoading && step === 4 && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center"
                >
                  <Card className="w-full max-w-md mx-4">
                    <CardContent className="p-8 text-center">
                      <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Regenerating Content</h3>
                      <p className="text-sm text-muted-foreground mb-4">{loadingStep}</p>
                      <Progress value={loadingProgress} className="w-full" />
                      <p className="text-xs text-muted-foreground mt-2">
                        Adapting your preparation for the selected interview type...
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="scripts"><FileText className="w-4 h-4 mr-2 hidden sm:block" />Scripts</TabsTrigger>
                  <TabsTrigger value="answers"><MessageSquare className="w-4 h-4 mr-2 hidden sm:block" />Answers</TabsTrigger>
                  <TabsTrigger value="questions"><Target className="w-4 h-4 mr-2 hidden sm:block" />Q&A</TabsTrigger>
                  <TabsTrigger value="behavioral"><Star className="w-4 h-4 mr-2 hidden sm:block" />STAR</TabsTrigger>
                </TabsList>

                <TabsContent value="scripts" className="space-y-4">
                  {[
                    { key: "two_min_intro", title: "2-Minute Introduction", icon: Clock, badge: "~250 words" },
                    { key: "five_min_walkthrough", title: "5-Minute Walkthrough", icon: FileText, badge: "~500 words" },
                    { key: "tell_me_about_yourself", title: "Tell Me About Yourself", icon: MessageSquare }
                  ].map(({ key, title, icon: Icon, badge }) => (
                    <Card key={key}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-accent rounded-lg"><Icon className="w-5 h-5 text-primary" /></div>
                            <div>
                              <h3 className="font-semibold">{title}</h3>
                              {badge && <span className="text-xs text-muted-foreground">{badge}</span>}
                            </div>
                          </div>
                          <CopyButton text={content[key]} sectionId={key} />
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{content[key]}</p>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="answers" className="space-y-4">
                  {[
                    { key: "why_you", title: "Why Should We Hire You?", icon: ThumbsUp },
                    { key: "why_this_company", title: "Why This Company?", icon: Briefcase },
                  ].map(({ key, title, icon: Icon }) => (
                    <Card key={key}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-accent rounded-lg"><Icon className="w-5 h-5 text-primary" /></div>
                            <h3 className="font-semibold">{title}</h3>
                          </div>
                          <CopyButton text={content[key]} sectionId={key} />
                        </div>
                        <p className="whitespace-pre-wrap text-sm">{content[key]}</p>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-accent rounded-lg"><ThumbsUp className="w-5 h-5 text-primary" /></div>
                        <h3 className="font-semibold">Your Strengths</h3>
                      </div>
                      <div className="space-y-2">
                        {Array.isArray(content.strengths) && content.strengths.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-secondary/30 rounded">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5" />
                            <span className="text-sm flex-1">{s}</span>
                            <CopyButton text={s} sectionId={`strength-${i}`} />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-accent rounded-lg"><ThumbsDown className="w-5 h-5 text-primary" /></div>
                          <h3 className="font-semibold">Weakness (Framed)</h3>
                        </div>
                        <CopyButton text={content.weakness} sectionId="weakness" />
                      </div>
                      <p className="text-sm">{content.weakness}</p>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="questions">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        Interview Questions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="single" collapsible>
                        {Array.isArray(content.interview_questions) && content.interview_questions.map((qa, i) => (
                          <AccordionItem key={i} value={`q-${i}`}>
                            <AccordionTrigger className="text-left">
                              <span className="flex items-center gap-2">
                                <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded">Q{i+1}</span>
                                {qa.question}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="pl-8 pr-4 py-2 flex items-start justify-between">
                                <p className="text-muted-foreground text-sm">{qa.answer}</p>
                                <CopyButton text={`Q: ${qa.question}\n\nA: ${qa.answer}`} sectionId={`qa-${i}`} />
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="behavioral">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-primary" />
                        STAR Format Answers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="single" collapsible>
                        {Array.isArray(content.behavioral_questions) && content.behavioral_questions.map((bq, i) => (
                          <AccordionItem key={i} value={`bq-${i}`}>
                            <AccordionTrigger className="text-left">
                              <span className="flex items-center gap-2">
                                <span className="bg-amber-500/10 text-amber-600 text-xs font-medium px-2 py-0.5 rounded">STAR</span>
                                {bq.question}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="pl-8 pr-4 py-2 space-y-3">
                                {[
                                  { key: "situation", label: "Situation", color: "bg-blue-500" },
                                  { key: "task", label: "Task", color: "bg-green-500" },
                                  { key: "action", label: "Action", color: "bg-amber-500" },
                                  { key: "result", label: "Result", color: "bg-purple-500" }
                                ].map(({ key, label, color }) => (
                                  <div key={key} className="flex items-start gap-2">
                                    <span className={`${color} text-white text-xs px-1.5 py-0.5 rounded mt-0.5`}>{label[0]}</span>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{label}</p>
                                      <p className="text-sm">{bq[key]}</p>
                                    </div>
                                  </div>
                                ))}
                                <div className="flex justify-end pt-2 border-t">
                                  <CopyButton 
                                    text={`Q: ${bq.question}\n\nS: ${bq.situation}\nT: ${bq.task}\nA: ${bq.action}\nR: ${bq.result}`} 
                                    sectionId={`star-${i}`} 
                                  />
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Chat Toggle and Actions */}
              <div className="mt-8 flex items-center justify-center gap-4">
                <Button variant="outline" onClick={() => {
                  setStep(1);
                  setActiveSession(prev => ({ ...prev, content: null }));
                }}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                <Button 
                  onClick={() => {
                    setShowChat(!showChat);
                    if (!showChat && activeSession) {
                      loadChatHistory(activeSession.session_id);
                    }
                  }}
                  variant={showChat ? "default" : "outline"}
                >
                  <Bot className="w-4 h-4 mr-2" />
                  {showChat ? "Hide Chat" : "Refine with AI"}
                </Button>
              </div>

              {/* AI Chat Panel */}
              <AnimatePresence>
                {showChat && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6"
                  >
                    <Card className="border-primary/20">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Bot className="w-5 h-5 text-primary" />
                          AI Refinement Chat
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          Ask me to refine your content - make it more concise, add metrics, change tone, etc.
                        </p>
                      </CardHeader>
                      <CardContent>
                        {/* Quick Prompts - Mode Specific */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {[...quickChatPrompts.slice(0, 3), ...getModePrompts().slice(0, 2)].map((prompt, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setChatInput(prompt);
                              }}
                            >
                              {prompt}
                            </Button>
                          ))}
                        </div>

                        {/* Chat Messages */}
                        <div className="h-64 overflow-y-auto border rounded-lg p-3 mb-4 bg-secondary/20">
                          {chatMessages.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Start a conversation to refine your content
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {chatMessages.map((msg, i) => (
                                <div
                                  key={i}
                                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                  <div
                                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                      msg.role === "user"
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-secondary"
                                    }`}
                                  >
                                    {msg.content}
                                  </div>
                                </div>
                              ))}
                              {isChatLoading && (
                                <div className="flex justify-start">
                                  <div className="bg-secondary rounded-lg px-3 py-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Chat Input */}
                        <div className="flex gap-2">
                          <Input
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Ask me to refine your content..."
                            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendChatMessage()}
                            disabled={isChatLoading}
                          />
                          <Button onClick={sendChatMessage} disabled={isChatLoading || !chatInput.trim()}>
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Career Insights Section - Embedded */}
              <div className="mt-8">
                <Card className="border-primary/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        Career Insights {canGenerateInsights() ? `for ${getJobContext().company}` : ""}
                      </CardTitle>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowInsights(!showInsights)}
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform ${showInsights ? 'rotate-180' : ''}`} />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {canGenerateInsights() 
                        ? `Prepare with company-specific insights for your ${activeSession?.job_title || jobData?.title || "position"} interview`
                        : "Company name required to generate insights. Please provide a specific company in your job description."}
                    </p>
                  </CardHeader>
                  
                  <AnimatePresence>
                    {showInsights && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <CardContent>
                          {!canGenerateInsights() ? (
                            <div className="text-center py-8 px-4">
                              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                              <p className="font-medium mb-2">Company Not Detected</p>
                              <p className="text-sm text-muted-foreground mb-4">
                                To generate strategic intelligence, we need to identify a real company from your job description.
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Make sure your job description includes a specific company name.
                              </p>
                            </div>
                          ) : (
                          <Tabs value={insightsTab} onValueChange={handleInsightTabChange}>
                            <TabsList className="grid w-full grid-cols-2 mb-4">
                              <TabsTrigger value="intelligence" className="text-xs">
                                <Building2 className="w-3 h-3 mr-1" />
                                Intelligence
                              </TabsTrigger>
                              <TabsTrigger value="salary" className="text-xs">
                                <DollarSign className="w-3 h-3 mr-1" />
                                Salary
                                {user?.role !== "premium" && !user?.is_admin && <Lock className="w-3 h-3 ml-1" />}
                              </TabsTrigger>
                            </TabsList>
                            
                            {/* Company & Sector Intelligence Tab */}
                            <TabsContent value="intelligence" className="mt-0">
                              {isLoadingInsights ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                  <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
                                  <p className="text-sm text-muted-foreground">Generating strategic intelligence for {getJobContext().company}...</p>
                                </div>
                              ) : intelligenceData?.error ? (
                                <div className="text-center py-8">
                                  <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                                  <p className="font-medium mb-2">Could Not Generate Intelligence</p>
                                  <p className="text-sm text-muted-foreground">{intelligenceData.error}</p>
                                </div>
                              ) : intelligenceData ? (
                                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                                  {/* Company Signals */}
                                  {intelligenceData.company_signals?.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-blue-500" />
                                        <h4 className="font-medium text-sm">Company Signals</h4>
                                      </div>
                                      {(intelligenceData.company_signals || []).slice(0, 4).map((signal, i) => (
                                        <div key={i} className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                                          <p className="text-sm font-medium">{signal.signal}</p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            <span className="text-blue-600">Why it matters for your role:</span> {signal.relevance_to_role}
                                          </p>
                                          {signal.source_url && (
                                            <a 
                                              href={signal.source_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-1.5 transition-colors"
                                              data-testid={`signal-source-${i}`}
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                              {signal.source_name || "Source"}
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Sector Trends */}
                                  {intelligenceData.sector_trends?.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-green-500" />
                                        <h4 className="font-medium text-sm">Sector Trends</h4>
                                        <Badge variant="outline" className="text-xs">{intelligenceData.sector}</Badge>
                                      </div>
                                      {(intelligenceData.sector_trends || []).slice(0, 3).map((trend, i) => (
                                        <div key={i} className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                                          <div className="flex items-start justify-between">
                                            <p className="text-sm font-medium">{trend.trend}</p>
                                            {trend.impact_level === "high" && (
                                              <Badge className="bg-green-600 text-xs">High Impact</Badge>
                                            )}
                                          </div>
                                          <p className="text-xs text-muted-foreground mt-1">{trend.description}</p>
                                          <p className="text-xs text-green-600 mt-1">→ {trend.relevance_to_role}</p>
                                          {trend.source_url && (
                                            <a 
                                              href={trend.source_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 text-xs text-green-500 hover:text-green-700 mt-1.5 transition-colors"
                                              data-testid={`trend-source-${i}`}
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                              {trend.source_name || "Source"}
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Talking Points */}
                                  {intelligenceData.talking_points?.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Lightbulb className="w-4 h-4 text-amber-500" />
                                        <h4 className="font-medium text-sm">Interview Talking Points</h4>
                                      </div>
                                      {(intelligenceData.talking_points || []).slice(0, 4).map((tp, i) => (
                                        <div key={i} className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                                          <p className="text-sm font-medium">{tp.point}</p>
                                          <p className="text-xs text-muted-foreground mt-1">{tp.how_to_use}</p>
                                          {tp.source_url && (
                                            <a 
                                              href={tp.source_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 mt-1.5 transition-colors"
                                              data-testid={`tp-source-${i}`}
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                              {tp.source_name || "Source"}
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Recommended Reading */}
                                  {intelligenceData.recommended_reading?.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <BookOpen className="w-4 h-4 text-purple-500" />
                                        <h4 className="font-medium text-sm">Recommended Reading</h4>
                                      </div>
                                      {(intelligenceData.recommended_reading || []).slice(0, 3).map((reading, i) => (
                                        <div key={i} className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                                          <div className="flex items-start justify-between gap-2">
                                            <p className="text-sm font-medium">{reading.title}</p>
                                            <Badge variant="outline" className="text-xs flex-shrink-0">{reading.source}</Badge>
                                          </div>
                                          <p className="text-xs text-purple-600 mt-1">
                                            <span className="font-medium">Why read this:</span> {reading.relevance_explanation}
                                          </p>
                                          {reading.url && (
                                            <a 
                                              href={reading.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 text-xs text-purple-500 hover:text-purple-700 mt-1.5 transition-colors"
                                              data-testid={`reading-source-${i}`}
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                              Read Article
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Networking Personas */}
                                  {intelligenceData.networking_personas?.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Network className="w-4 h-4 text-cyan-500" />
                                        <h4 className="font-medium text-sm">Key People to Network With</h4>
                                      </div>
                                      <p className="text-xs text-muted-foreground mb-2">
                                        Suggested profiles to look for — no names, just role archetypes
                                      </p>
                                      {(intelligenceData.networking_personas || []).slice(0, 4).map((persona, i) => (
                                        <div key={i} className="p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                                          <div className="flex items-start justify-between mb-2">
                                            <div>
                                              <p className="text-sm font-medium">{persona.likely_title}</p>
                                              <p className="text-xs text-muted-foreground">{persona.department}</p>
                                            </div>
                                            <Badge variant="outline" className="text-xs capitalize">
                                              {(persona.persona_type || '').replace(/_/g, ' ')}
                                            </Badge>
                                          </div>
                                          <p className="text-xs text-cyan-600 mb-2">{persona.why_this_person_matters}</p>
                                          
                                          {persona.questions_to_ask?.length > 0 && (
                                            <div className="mb-2">
                                              <p className="text-xs font-medium text-muted-foreground mb-1">Questions to Ask:</p>
                                              <ul className="text-xs text-muted-foreground space-y-0.5">
                                                {(persona.questions_to_ask || []).slice(0, 2).map((q, qi) => (
                                                  <li key={qi}>• {q}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          
                                          {persona.linkedin_search_query && (
                                            <Button 
                                              variant="outline" 
                                              size="sm" 
                                              className="w-full h-7 text-xs mt-2"
                                              onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(persona.linkedin_search_query)}`, '_blank')}
                                            >
                                              <Linkedin className="w-3 h-3 mr-1" />
                                              Find on LinkedIn
                                            </Button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <Button 
                                    variant="outline" 
                                    className="w-full" 
                                    onClick={fetchCompanyIntelligence}
                                  >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh Intelligence
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                  <p className="font-medium mb-2">Company & Sector Intelligence</p>
                                  <p className="text-sm text-muted-foreground mb-4">
                                    Get strategic context about {activeSession?.company_name || "the company"} and the sector
                                  </p>
                                  <Button onClick={fetchCompanyIntelligence} data-testid="generate-intelligence-btn">
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Generate Intelligence
                                  </Button>
                                </div>
                              )}
                            </TabsContent>
                            
                            {/* Salary Tab */}
                            <TabsContent value="salary" className="mt-0">
                              {isLoadingInsights ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                  <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
                                  <p className="text-sm text-muted-foreground">Fetching salary data...</p>
                                </div>
                              ) : salaryData?.isPremiumLocked ? (
                                <div className="text-center py-8">
                                  <Crown className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                                  <p className="font-medium mb-2">Premium Feature</p>
                                  <p className="text-sm text-muted-foreground mb-4">
                                    Upgrade to access salary benchmarks for {activeSession?.job_title}
                                  </p>
                                  <Button onClick={() => setShowUpgradeModal(true)}>
                                    <Crown className="w-4 h-4 mr-2" />
                                    Upgrade to Premium
                                  </Button>
                                </div>
                              ) : salaryData ? (
                                <div className="space-y-4">
                                  <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-1">Estimated Range</p>
                                    <p className="text-2xl font-bold">
                                      {salaryData.salary_range?.currency === "EUR" ? "€" : "$"}
                                      {salaryData.salary_range?.min?.toLocaleString()} - {salaryData.salary_range?.max?.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Median: {salaryData.salary_range?.currency === "EUR" ? "€" : "$"}{salaryData.salary_range?.median?.toLocaleString()}
                                    </p>
                                  </div>
                                  
                                  {salaryData.factors && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">Key Factors</p>
                                      <ul className="text-xs text-muted-foreground space-y-1">
                                        {(salaryData.factors || []).slice(0, 4).map((f, i) => (
                                          <li key={i}>• {f}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {salaryData.negotiation_tips && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">Negotiation Tips</p>
                                      <ul className="text-xs text-muted-foreground space-y-1">
                                        {(salaryData.negotiation_tips || []).slice(0, 3).map((t, i) => (
                                          <li key={i}>💡 {t}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                  <p className="text-sm text-muted-foreground mb-4">
                                    Get salary benchmarks for {activeSession?.job_title}
                                  </p>
                                  <Button onClick={fetchSalaryBenchmark}>
                                    Get Salary Data
                                  </Button>
                                </div>
                              )}
                            </TabsContent>
                          </Tabs>
                          )}
                        </CardContent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </div>
              
              {/* CV Side Panel - Desktop Only */}
              <AnimatePresence>
                {showCVPanel && activeSession?.cv_text && (
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    className="hidden md:block fixed top-20 right-4 w-96 h-[calc(100vh-6rem)] bg-background border rounded-lg shadow-lg overflow-hidden z-40"
                    data-testid="cv-side-panel"
                  >
                    <div className="p-3 border-b bg-secondary/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileTextIcon className="w-4 h-4 text-primary" />
                          <span className="font-medium text-sm">Your CV</span>
                          {activeSession.cv_filename && (
                            <Badge variant="outline" className="text-xs">
                              {activeSession.cv_filename.split('.').pop()?.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setShowCVPanel(false)} data-testid="close-cv-panel">
                          <PanelRightClose className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* View Mode Toggle */}
                      <div className="flex gap-1">
                        <Button
                          variant={cvViewMode === "document" ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs h-7"
                          onClick={() => setCvViewMode("document")}
                          disabled={!cvDocumentUrl && !isLoadingCvDocument}
                          data-testid="cv-view-document-btn"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Original
                        </Button>
                        <Button
                          variant={cvViewMode === "text" ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs h-7"
                          onClick={() => setCvViewMode("text")}
                          data-testid="cv-view-text-btn"
                        >
                          <FileTextIcon className="w-3 h-3 mr-1" />
                          Extracted
                        </Button>
                      </div>
                    </div>
                    
                    <div className="h-[calc(100%-90px)] overflow-hidden">
                      {cvViewMode === "document" ? (
                        isLoadingCvDocument ? (
                          <div className="flex flex-col items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
                            <p className="text-sm text-muted-foreground">Loading document...</p>
                          </div>
                        ) : cvDocumentUrl?.url ? (
                          <div className="h-full w-full">
                            {cvDocumentUrl.content_type?.includes('pdf') ? (
                              <iframe
                                src={`${cvDocumentUrl.url}#toolbar=0&navpanes=0`}
                                className="w-full h-full border-0"
                                title="CV Document"
                                data-testid="cv-document-iframe"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                                <FileText className="w-12 h-12 text-muted-foreground mb-3" />
                                <p className="text-sm font-medium mb-2">{cvDocumentUrl.filename}</p>
                                <p className="text-xs text-muted-foreground mb-4">
                                  DOCX preview not available in browser
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(cvDocumentUrl.url, '_blank')}
                                  data-testid="cv-download-btn"
                                >
                                  Download Original
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                            <AlertCircle className="w-8 h-8 text-muted-foreground mb-3" />
                            <p className="text-sm text-muted-foreground mb-2">
                              Original document not available
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCvViewMode("text")}
                            >
                              View Extracted Text
                            </Button>
                          </div>
                        )
                      ) : (
                        <div className="p-4 h-full overflow-y-auto">
                          <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground leading-relaxed">
                            {activeSession.cv_text}
                          </pre>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            /* Workflow Steps */
            <div className="max-w-2xl mx-auto px-4 py-8">
              <AnimatePresence mode="wait">
                {/* Step 1: Upload CV */}
                {step === 1 && (
                  <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                    <Card>
                      <CardContent className="p-8">
                        <div className="text-center mb-6">
                          <Upload className="w-12 h-12 text-primary mx-auto mb-4" />
                          <h2 className="text-xl font-semibold mb-2">Upload Your CV</h2>
                          <p className="text-muted-foreground text-sm">We'll extract your skills and experience</p>
                        </div>

                        {error?.type === "cv" && (
                          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-destructive">{error.message}</p>
                          </div>
                        )}

                        <div
                          {...getRootProps()}
                          className={`dropzone ${isDragActive ? "dropzone-active" : ""} ${cvFile && !error ? "dropzone-accepted" : ""}`}
                        >
                          <input {...getInputProps()} />
                          {isLoading ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="w-10 h-10 text-primary mb-3 animate-spin" />
                              <p className="text-sm text-muted-foreground">{loadingStep}</p>
                              <Progress value={loadingProgress} className="w-40 mt-2" />
                            </div>
                          ) : cvFile ? (
                            <div className="flex flex-col items-center">
                              <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
                              <p className="font-medium text-sm">{cvFile.name}</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <Upload className="w-10 h-10 text-muted-foreground mb-3" />
                              <p className="font-medium text-sm">{isDragActive ? "Drop here" : "Drag & drop your CV"}</p>
                              <p className="text-xs text-muted-foreground mt-1">PDF or DOCX</p>
                            </div>
                          )}
                        </div>
                        
                        {/* GDPR Privacy Notice */}
                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-start gap-2">
                            <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-blue-700 dark:text-blue-300">
                              <p className="font-medium mb-1">Your CV is secure</p>
                              <p className="text-blue-600 dark:text-blue-400">
                                Your document is encrypted and stored securely. We never share your CV with third parties. 
                                You can delete your data anytime from your account settings.{" "}
                                <a href="/privacy" className="underline hover:text-blue-800 dark:hover:text-blue-200">Privacy Policy</a>
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Step 2: Job Description */}
                {step === 2 && (
                  <motion.div key="step2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                    <Card>
                      <CardContent className="p-8">
                        <div className="text-center mb-6">
                          <Briefcase className="w-12 h-12 text-primary mx-auto mb-4" />
                          <h2 className="text-xl font-semibold mb-2">Add Job Description</h2>
                          <p className="text-muted-foreground text-sm">Paste the job URL or description</p>
                        </div>

                        {error?.type === "job" && (
                          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-destructive">{error.message}</p>
                          </div>
                        )}

                        <Tabs value={jobInputType} onValueChange={setJobInputType} className="mb-4">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" />URL</TabsTrigger>
                            <TabsTrigger value="text"><FileText className="w-4 h-4 mr-2" />Paste</TabsTrigger>
                          </TabsList>
                          <TabsContent value="url" className="mt-4">
                            <Input placeholder="https://linkedin.com/jobs/..." value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} disabled={isLoading} />
                          </TabsContent>
                          <TabsContent value="text" className="mt-4">
                            <Textarea placeholder="Paste the job description..." value={jobText} onChange={(e) => setJobText(e.target.value)} rows={6} disabled={isLoading} />
                          </TabsContent>
                        </Tabs>

                        {isLoading && (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Loader2 className="w-4 h-4 animate-spin text-primary" />
                              <span className="text-sm text-muted-foreground">{loadingStep}</span>
                            </div>
                            <Progress value={loadingProgress} />
                          </div>
                        )}

                        <div className="flex gap-3">
                          <Button variant="outline" onClick={() => setStep(1)} disabled={isLoading} className="flex-1">Back</Button>
                          <Button onClick={handleJobSubmit} disabled={isLoading} className="flex-1">
                            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                            Continue
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Step 3: Generate */}
                {step === 3 && (
                  <motion.div key="step3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                    <Card>
                      <CardContent className="p-8">
                        <div className="text-center mb-6">
                          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
                          <h2 className="text-xl font-semibold mb-2">Generate Interview Prep</h2>
                          <p className="text-muted-foreground text-sm">Choose interview type and generate</p>
                        </div>

                        {/* Rate limit warning */}
                        {!user?.can_generate && !user?.is_admin && user?.role !== "premium" && (
                          <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Crown className="w-5 h-5 text-amber-500" />
                              <span className="font-medium text-amber-600">Free limit reached</span>
                            </div>
                            <p className="text-sm text-amber-600/80 mb-3">
                              You've used all {FREE_TIER_LIMIT} free interview preps. Upgrade to continue.
                            </p>
                            <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => setShowUpgradeModal(true)}>
                              <Crown className="w-4 h-4 mr-2" />
                              Upgrade to Premium
                            </Button>
                          </div>
                        )}

                        {error?.type === "generate" && (
                          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-destructive">{error.message}</p>
                            </div>
                            {error.canRetry && (
                              <Button variant="outline" size="sm" onClick={handleGenerate} className="mt-2">
                                <RefreshCw className="w-4 h-4 mr-2" />Try Again
                              </Button>
                            )}
                          </div>
                        )}

                        <div className="bg-secondary/50 rounded-lg p-4 mb-6">
                          <div className="flex items-start gap-3 mb-2">
                            <FileText className="w-4 h-4 text-primary mt-0.5" />
                            <div>
                              <p className="text-sm font-medium">{cvFile?.name}</p>
                              {cvData?.skills?.length > 0 && (
                                <p className="text-xs text-muted-foreground">Skills: {cvData.skills.slice(0, 4).join(", ")}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Briefcase className="w-4 h-4 text-primary mt-0.5" />
                            <div>
                              <p className="text-sm font-medium">{jobData?.title}</p>
                              <p className="text-xs text-muted-foreground">{jobData?.company}</p>
                            </div>
                          </div>
                        </div>

                        <div className="mb-6">
                          <p className="text-sm font-medium mb-2">Interview Type</p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { value: "general", label: "General", icon: MessageSquare },
                              { value: "hr", label: "HR Screen", icon: Target },
                              { value: "technical", label: "Technical", icon: FileTextIcon },
                              { value: "manager", label: "Manager", icon: Briefcase }
                            ].map(({ value, label, icon: Icon }) => (
                              <button
                                key={value}
                                onClick={() => setInterviewType(value)}
                                disabled={isLoading}
                                className={`p-3 rounded-lg border text-left transition-all ${
                                  interviewType === value 
                                    ? "border-primary bg-accent text-primary" 
                                    : "border-border hover:border-primary/50"
                                }`}
                              >
                                <Icon className="w-4 h-4 mb-1" />
                                <p className="text-sm font-medium">{label}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Interview Mode Selector */}
                        <div className="mb-6">
                          <p className="text-sm font-medium mb-2">Interviewer Type</p>
                          <p className="text-xs text-muted-foreground mb-3">Select who you're preparing for</p>
                          <div className="grid grid-cols-1 gap-2">
                            {[
                              { value: "hr_recruiter", label: "HR / Recruiter Screen", icon: Users, focus: "Soft skills, motivation, cultural fit" },
                              { value: "hiring_manager", label: "Hiring Manager", icon: UserCheck, focus: "Ownership, impact, leadership" },
                              { value: "technical_expert", label: "Technical Expert", icon: Code, focus: "Technical depth, tools, architecture" }
                            ].map(({ value, label, icon: Icon, focus }) => (
                              <button
                                key={value}
                                onClick={() => setInterviewMode(value)}
                                disabled={isLoading}
                                className={`p-3 rounded-lg border text-left transition-all ${
                                  interviewMode === value 
                                    ? "border-primary bg-primary/5 ring-1 ring-primary" 
                                    : "border-border hover:border-primary/50"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-full ${interviewMode === value ? 'bg-primary text-white' : 'bg-secondary'}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{label}</p>
                                    <p className="text-xs text-muted-foreground">{focus}</p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <Button variant="outline" onClick={() => setStep(2)} disabled={isLoading} className="flex-1">Back</Button>
                          <Button 
                            onClick={handleGenerate} 
                            disabled={isLoading || (!user?.can_generate && !user?.is_admin && user?.role !== "premium")} 
                            className="flex-1"
                          >
                            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                            Generate
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Loading Overlay */}
              {isLoading && step === 3 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <Card className="w-80">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Generating Content</h3>
                      <p className="text-sm text-muted-foreground mb-3">{loadingStep}</p>
                      <Progress value={loadingProgress} />
                      <p className="text-xs text-muted-foreground mt-3">This may take up to 2 minutes...</p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Support Modal */}
      <Dialog open={showSupportModal} onOpenChange={setShowSupportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              Contact Support
            </DialogTitle>
            <DialogDescription>
              Report an issue or send feedback. We'll get back to you soon.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select value={supportCategory} onValueChange={setSupportCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">
                    <div className="flex items-center gap-2">
                      <Bug className="w-4 h-4 text-red-500" />
                      Bug Report
                    </div>
                  </SelectItem>
                  <SelectItem value="feature">Feature Request</SelectItem>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input 
                placeholder="Brief summary of your issue"
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Message</label>
              <textarea 
                className="w-full min-h-[100px] p-3 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Describe your issue or feedback in detail..."
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSupportModal(false)}>
              Cancel
            </Button>
            <Button onClick={submitSupportTicket} disabled={isSubmittingTicket}>
              {isSubmittingTicket && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppDashboard;
